import React, {useRef, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from 'modules';
import {getProgressBarTimeByPx} from 'lib/utils';
import {getFileIndexByTime, getTotalDurationByFiles} from 'lib/playUtils';
import {IVodInfoData} from 'lib/api/vodInfo';
import EditSnapshot from 'components/edit/EditSnapshot';

/**
 * 편집 영역 > 스냅샷 컨테이너
 */

let snapshotUrl = '';
let snapshotWidth = 80; // 캔버스에 그릴 스냅샷 너비

const EditSnapshotContainer = () => {
  const {vodData, totalDuration, fileLists, sectionIdx, sectionLists} =
    useSelector(({vodInfo, video, file, timeline}: RootState) => ({
      vodData: vodInfo.titleFileInfo.data,
      totalDuration: video.totalDuration,
      fileLists: file.list,
      sectionIdx: timeline.sectionIdx,
      sectionLists: timeline.sectionLists,
    }));
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const SNAPSHOT_DURATION = 3; // 스냅샷은 3초 간격으로 1장씩 뽑음
  const SNAPSHOT_ROW_CNT = 10;
  const SNAPSHOT_COLUMN_CNT = 10;

  // const SNAPSHOT_HEIGHT = 50; // 캔버스 높이
  // const SNAPSHOT_ORG_WIDTH = 128; // 스냅샷 원본 너비
  // const SNAPSHOT_ORG_HEIGHT = 72; // 스냅샷 원본 높이
  const SNAPSHOT_POSTFIX = '_t';

  /**
   * 리듀서에서 VOD 정보 읽어옴
   */
  useEffect(() => {
    const {response} = vodData as IVodInfoData;
    const {snapshot_url, resolution} = response.info; // 스냅샷 URL
    snapshotUrl = snapshot_url;

    if (!resolution) return;

    const aResolution = resolution.split('x');
    if (!aResolution[0] || !aResolution[1]) return;

    if (Number(aResolution[0]) < Number(aResolution[1])) {
      // 높이가 더 클 경우
      snapshotWidth = 55;
    }
  }, [vodData]);

  /**
   * 스냅샷 그리는 useEffect
   */
  useEffect(() => {
    if (sectionLists[0].endTime === 0) return;
    if (!canvasRef.current) return;
    if (totalDuration <= 0) return;
    if (!sectionLists[sectionIdx]) return;

    const canvasWidth = canvasRef.current.width; // 스냅샷 캔버스 너비
    const sectionList = sectionLists[sectionIdx];
    let dX = 0;

    for (let px = 0; px < canvasWidth; px += snapshotWidth) {
      const drawObj = getDrawSnapshotInfo(
        px,
        canvasWidth,
        sectionList.startTime,
        sectionList.endTime,
      );
      drawSnapshot(drawObj.url, drawObj.snapshotTime, dX);
      dX += snapshotWidth;
    }

    // 마지막 px 너비 값 까지 그려줘야 끝까지 그려짐!!
    const drawObj = getDrawSnapshotInfo(
      canvasWidth,
      canvasWidth,
      sectionList.startTime,
      sectionList.endTime,
    );

    drawSnapshot(drawObj.url, drawObj.snapshotTime, dX);
  }, [sectionLists, totalDuration, sectionIdx]);

  /**
   * 시간 값으로 스냅샷 idx 가져오는 함수
   * @param time 시간
   * @param duration 현재 시간까지의 파일 총 duration
   */
  const getSnapshotIndexByTime = (time: number, duration: number) => {
    const t = time - duration;
    return Math.floor(t / SNAPSHOT_DURATION);
  };

  /**
   * 스냅샷 좌표 가져오는 함수
   * @param idx 스냅샷 idx
   */
  const getSnapshotPosition = (idx: number, width: number, height: number) => {
    const rowIdx = Math.floor(idx / SNAPSHOT_COLUMN_CNT);
    const columnIdx = idx - rowIdx * SNAPSHOT_ROW_CNT;

    return {
      x: columnIdx * width,
      y: rowIdx * height,
    };
  };

  /**
   * 캔버스에 스냅샷 그리기 위한 정보 가져오는 함수
   * @param px 스냅샷은 80px씩 그림
   * @param canvasWidth 캔버스 너비
   * @return { url, snapshotTime }
   */
  const getDrawSnapshotInfo = (
    px: number,
    canvasWidth: number,
    startTime: number,
    endTime: number,
  ) => {
    const time = getProgressBarTimeByPx(px, canvasWidth, startTime, endTime); // 스냅샷 px 값으로 시간 가져옴
    const fileIdx = getFileIndexByTime(fileLists, time); // 현재 시간의 파일 idx
    const duration = getTotalDurationByFiles(fileLists, fileIdx); // 현재 시간까지 총 duration
    const snapshotTime = getSnapshotIndexByTime(time, duration); // 현재 시간의 스냅샷 idx
    const column = Number(
      Math.floor(snapshotTime / (SNAPSHOT_ROW_CNT * SNAPSHOT_COLUMN_CNT)) + 1,
    ); // 현재 시간의 스냅샷 column
    const url = `${snapshotUrl}${fileLists[fileIdx].file_info_key}${SNAPSHOT_POSTFIX}&column=${column}`; // 최종 스냅샷 URL
    return {
      url,
      snapshotTime,
    };
  };

  /**
   * 캔버스에 스냅샷 그리는 함수
   */
  const drawSnapshot = (url: string, idx: number, dX: number) => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const canvasHeight = canvasRef.current.height; // 캔버스 높이

    // 스냅샷은 10x10 매트릭스로 1장 당 3초씩 있음
    // 한 이미지에 최대 100장 이므로, % 100 연산하여 이미지당 좌표 구해줌
    idx %= 100;

    const img = new Image();
    img.src = url;

    img.onload = () => {
      const calcWidth = img.naturalWidth / SNAPSHOT_ROW_CNT;
      const calcHeight = img.naturalHeight / SNAPSHOT_COLUMN_CNT;
      const position = getSnapshotPosition(idx, calcWidth, calcHeight);
      ctx.drawImage(
        img,
        position.x,
        position.y,
        calcWidth,
        calcHeight,
        dX,
        0,
        snapshotWidth,
        canvasHeight,
      );
    };
  };

  return <EditSnapshot canvasRef={canvasRef} />;
};

export default EditSnapshotContainer;
